import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { 
  Building2, Edit2, Trash2, User, Phone, Mail, 
  MapPin, Server, CheckCircle 
} from 'lucide-react';

const ClientCard = ({ 
  client, 
  isSelected, 
  onSelect, 
  onEdit, 
  onDelete,
  onActivate
}) => {
  return (
    <Card 
      className={`hover:shadow-md transition-all cursor-pointer ${
        isSelected ? 'ring-2 ring-emerald-500 bg-emerald-50/30' : ''
      }`}
      data-testid={`client-card-${client.id}`}
    >
      <CardContent className="pt-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onSelect(client.id)}
              onClick={(e) => e.stopPropagation()}
              data-testid={`client-checkbox-${client.id}`}
            />
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold">{client.name}</h3>
              {client.short_name && (
                <p className="text-sm text-slate-500">{client.short_name}</p>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={(e) => { e.stopPropagation(); onEdit(client); }}
              data-testid={`edit-client-${client.id}`}
            >
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              className="text-red-500 hover:text-red-700"
              onClick={(e) => { e.stopPropagation(); onDelete(client.id); }}
              data-testid={`delete-client-${client.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          {client.pan && (
            <div className="flex items-center gap-2 text-slate-600">
              <Badge variant="outline" className="font-mono">{client.pan}</Badge>
            </div>
          )}
          {client.contact_person && (
            <div className="flex items-center gap-2 text-slate-600">
              <User className="w-4 h-4" />
              {client.contact_person}
            </div>
          )}
          {client.phone && (
            <div className="flex items-center gap-2 text-slate-600">
              <Phone className="w-4 h-4" />
              {client.phone}
            </div>
          )}
          {client.email && (
            <div className="flex items-center gap-2 text-slate-600">
              <Mail className="w-4 h-4" />
              {client.email}
            </div>
          )}
          {client.address && (
            <div className="flex items-center gap-2 text-slate-600">
              <MapPin className="w-4 h-4" />
              <span className="truncate">{client.address}</span>
            </div>
          )}
          {client.tally_company && (
            <div className="flex items-center gap-2 text-slate-600">
              <Server className="w-4 h-4" />
              {client.tally_company}
            </div>
          )}
        </div>

        <div className="mt-3 pt-3 border-t flex justify-between items-center">
          <Badge variant={client.is_active ? "default" : "secondary"}>
            {client.is_active ? "Active" : "Inactive"}
          </Badge>
          <Button 
            variant="outline" 
            size="sm"
            onClick={(e) => { e.stopPropagation(); onActivate(client); }}
            data-testid={`activate-client-${client.id}`}
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            Set Active
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ClientCard;
